/**
 * Hook for synchronous blob downloads where the server returns the file
 * directly in the HTTP response (e.g., ZIP, CSV, XLSX).
 *
 * This is the counterpart to `useDownloadResource`, which handles async
 * downloads via Pusher notifications (POST → subscribe → wait → open URL).
 * Use `useBlobDownload` when the server generates the file inline and
 * returns it as a binary response within the request timeout.
 *
 * @example
 * const { isDownloading, triggerDownload } = useBlobDownload({
 *   url: `/api/exports/${id}`,
 *   filename: () => `export_${id}.zip`,
 *   onError: (msg) => showToast(msg),
 * });
 */
import { useState, useCallback } from 'react';
import { downloadBlob } from 'utils/download-blob';

interface UseBlobDownloadOptions {
  /** The endpoint URL to fetch the blob from. */
  url: string;
  /** Static filename string or function that returns one at download time. */
  filename: string | (() => string);
  /** HTTP method (default: POST). */
  method?: 'GET' | 'POST';
  /** Optional JSON body to include with POST requests. */
  body?: Record<string, unknown>;
  /** Callback invoked with an error message string on failure. */
  onError?: (message: string) => void;
}

export interface UseBlobDownloadReturn {
  isDownloading: boolean;
  triggerDownload: () => Promise<void>;
}

export function useBlobDownload({
  url,
  filename,
  method = 'POST',
  body,
  onError,
}: UseBlobDownloadOptions): UseBlobDownloadReturn {
  const [isDownloading, setIsDownloading] = useState(false);

  const triggerDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/octet-stream',
          'X-CSRF-Token': csrfToken,
          ...(body && { 'Content-Type': 'application/json' }),
        },
        ...(body && { body: JSON.stringify(body) }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(
          errorBody?.error || `Download failed (${response.status})`
        );
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const serverFilename = disposition
        ?.match(/filename="?([^";\n]+)"?/)?.[1]
        ?.trim();
      const fallbackFilename =
        typeof filename === 'function' ? filename() : filename;
      downloadBlob(blob, serverFilename || fallbackFilename);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  }, [url, filename, method, body, onError]);

  return { isDownloading, triggerDownload };
}
